import { useEffect, useState } from 'react';

import { Button } from '@dbt-labs/sourdough';

import { shouldAutoRenderDag } from '../dag/dagComplexityAnalysis';
import { DagProps, DagStats, DbtDagNode } from '../dag/DagInterfaces';
import {
  generateDagMap,
  getDagNodeStats,
  stripOutUnusedEdges,
} from '../dag/internalDag';
import { DagEntityCounts, DagMap } from '../dag/InternalDagInterfaces';
import { useSavedComplexity } from '../dag/savedComplexity';
import { useDagContext } from '../dbt-dag';
import { useStableArray } from '../hooks/useStableArray';
import { useInternalDagContext } from '../providers/InternalDagProvider';
import { isDagreLayoutCached } from '../utils/dagre';
import { IdleDag } from './IdleDag';
import { ValidatedDag } from './ValidatedDag';
const LAYOUT_TIME_TRESHOLD_MILLIS = 2500;

/**
 * This will render one of the following views:
 * * Loading state
 * * Error state
 * * Ask users if they want to render a large DAG
 * * Or the DAG
 */
export const DagRouter = <TDbtDagNode extends DbtDagNode>({
  nodes: _unstableNodes,
  status = 'success',
  showLoadingScreen,
  errorMessages,
  activeDbtCloudProject,
  primaryNodeIds: _primaryNodeIds,
  onComplexityWarn,
  grain,
  onDagLayoutResult,
  ...otherProps
}: DagProps<TDbtDagNode>) => {
  const { setLensContext, setLargeDagWarningShown } = useDagContext();
  const nodes = useStableArray(_unstableNodes)!;
  const primaryNodeIds = useStableArray(_primaryNodeIds);
  // If true, the dag will not be auto rendered.
  const [warnDagComplexity, setWarnDagComplexity] = useState<DagEntityCounts | null>(
    null,
  );
  const [shouldRenderComplexDag, setShouldRenderComplexDag] = useState(false);
  const [validatedDagMap, setValidatedDagMap] = useState<DagMap<TDbtDagNode> | null>(
    null,
  );
  const { onLayoutComplete, savedComplexity, setPendingComplexity } =
    useSavedComplexity(LAYOUT_TIME_TRESHOLD_MILLIS);
  const { nodeType } = useInternalDagContext();

  useEffect(() => {
    setLensContext((prev) => ({
      ...prev,
      queryCountMin: Math.min(...nodes.map((node) => node.usageQueryCount || 0)),
      queryCountMax: Math.max(...nodes.map((node) => node.usageQueryCount || 0)),
      costMin: Math.min(...nodes.map((node) => node.data?.cost || 0)),
      costMax: Math.max(...nodes.map((node) => node.data?.cost || 0)),
    }));
  }, [nodes, setLensContext]);

  useEffect(() => {
    // We need to strip out any unused edges to make Dagre happy.
    const validatedDagMap = stripOutUnusedEdges(generateDagMap(nodeType, nodes));
    const dagStats = getDagNodeStats(nodes);
    const isLayoutCached = isDagreLayoutCached(validatedDagMap, { grain });
    const { shouldAutoRender, complexity } = shouldAutoRenderDag(
      dagStats,
      savedComplexity,
    );
    setPendingComplexity(complexity);

    setShouldRenderComplexDag(false);
    if (isLayoutCached || shouldAutoRender) {
      setWarnDagComplexity(null);
    } else {
      onComplexityWarn?.();
      setWarnDagComplexity(dagStats);
      setLargeDagWarningShown(true);
    }
    setValidatedDagMap(validatedDagMap);
  }, [
    nodes,
    savedComplexity,
    setPendingComplexity,
    onComplexityWarn,
    otherProps.nodeType,
    nodeType,
    grain,
    _unstableNodes,
    setLargeDagWarningShown,
  ]);

  if (
    status !== 'success' ||
    showLoadingScreen ||
    nodes.length === 0 ||
    validatedDagMap === null
  ) {
    return (
      <IdleDag
        status={status}
        showLoadingScreen={showLoadingScreen}
        errorMessages={errorMessages}
      />
    );
  }
  // The commented condition is probably more readable
  // if (!shouldAutoRender && !isLayoutCached && !shouldRenderComplexDag) {
  if (warnDagComplexity && !shouldRenderComplexDag) {
    return (
      <IdleDag
        status={'success'}
        errorMessages={[
          `Warning! This lineage graph with ${warnDagComplexity.nodeCount} nodes and ${warnDagComplexity.edgeCount} edges may be too large to render. This can crash your browser tab.`,
          'To proceed, hit the Render Lineage button.',
        ]}
      >
        <Button
          type="error"
          text="Render Lineage"
          className="mt-3"
          onClick={() => {
            setShouldRenderComplexDag(true);
          }}
        />
      </IdleDag>
    );
  }
  const validatedProps = {
    ...otherProps,
    primaryNodeIds,
    disableMagnifiedNode: otherProps.disableMagnifiedNode,
    onRenderComplete: (stats: DagStats) => {
      if (stats.layoutDurationMillis !== null) {
        onLayoutComplete(stats.layoutDurationMillis);
      }
      otherProps.onRenderComplete?.(stats);
    },
  };
  const forceRenderComplexDag = !!warnDagComplexity && shouldRenderComplexDag;
  return (
    <ValidatedDag
      validatedDagMap={validatedDagMap}
      forceRenderComplexDag={forceRenderComplexDag}
      activeDbtCloudProject={activeDbtCloudProject}
      grain={grain}
      onDagLayoutResult={onDagLayoutResult}
      {...validatedProps}
    />
  );
};
